/**
 * TimestampDisplay Component
 *
 * Renders a timestamp with UTC as the primary display and local time as the hover tooltip.
 * Standardizes timestamp display across all card domains (moderation, content search,
 * classification testing, internal audit).
 */

export interface TimestampDisplayProps {
  /** ISO timestamp string or Date object */
  timestamp: string | Date;
  /** IANA timezone for primary display (e.g., 'America/Los_Angeles'). Defaults to 'UTC'. */
  displayTimezone?: string;
  /** Additional CSS class name */
  className?: string;
}

/**
 * Formats a timestamp in the given IANA timezone.
 * @param timezone - IANA timezone identifier (defaults to 'UTC')
 * @returns e.g., "Feb 25, 2020, 9:07 AM UTC"
 */
export function formatTimestamp(timestamp: string | Date, timezone = 'UTC'): string {
  try {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;

    if (isNaN(date.getTime())) return 'Invalid date';

    const formatted = date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone,
      timeZoneName: 'short',
    });

    return formatted;
  } catch {
    return 'Invalid date';
  }
}

/** @deprecated Use `formatTimestamp(timestamp, 'UTC')` instead */
export function formatTimestampUTC(timestamp: string | Date): string {
  return formatTimestamp(timestamp, 'UTC');
}

/**
 * Formats a timestamp to local timezone display string.
 * @returns e.g., "Feb 25, 2020, 9:07 AM EST"
 */
export function formatTimestampLocal(timestamp: string | Date): string {
  return formatTimestamp(timestamp, Intl.DateTimeFormat().resolvedOptions().timeZone);
}

/**
 * Displays a timestamp with the specified timezone as the primary text and local time as hover tooltip.
 * Defaults to UTC when no displayTimezone is provided.
 */
export function TimestampDisplay({ timestamp, displayTimezone = 'UTC', className }: TimestampDisplayProps) {
  const displayText = formatTimestamp(timestamp, displayTimezone);
  const localText = formatTimestampLocal(timestamp);

  return (
    <span className={className} title={localText}>
      {displayText}
    </span>
  );
}

TimestampDisplay.displayName = 'TimestampDisplay';
